import React, { useState } from 'react';
import {
  Copy,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Flame,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Session, ExerciseDefinition } from '../types';
import { usePlanStore } from '../store/usePlanStore';
import { DAYS } from '../utils/helpers';
import ExerciseRow from './ExerciseRow';
import ExerciseSearch from './ExerciseSearch';

interface Props {
  weekId: string;
  session: Session;
}

export default function SessionCard({ weekId, session }: Props) {
  const {
    updateSession,
    removeSession,
    duplicateSession,
    addExercise,
    updateExercise,
    removeExercise,
    reorderExercises,
    addWarmupExercise,
    updateWarmupExercise,
    removeWarmupExercise,
    reorderWarmupExercises,
  } = usePlanStore();

  const [collapsed, setCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showWarmupSearch, setShowWarmupSearch] = useState(false);
  const [warmupCollapsed, setWarmupCollapsed] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = session.exercises.findIndex((e) => e.id === active.id);
    const newIdx = session.exercises.findIndex((e) => e.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      reorderExercises(weekId, session.id, oldIdx, newIdx);
    }
  };

  const handleWarmupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const warmups = session.warmupExercises || [];
    const oldIdx = warmups.findIndex((e) => e.id === active.id);
    const newIdx = warmups.findIndex((e) => e.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      reorderWarmupExercises(weekId, session.id, oldIdx, newIdx);
    }
  };

  const handleAddExercise = (def: ExerciseDefinition) => {
    addExercise(weekId, session.id, def);
  };

  const handleAddWarmupExercise = (def: ExerciseDefinition) => {
    addWarmupExercise(weekId, session.id, def);
  };

  const warmups = session.warmupExercises || [];
  const totalCount = session.exercises.length + warmups.length;

  return (
    <div
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden transition-all"
    >
      {/* ── Session Header ───────────────── */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Day selector */}
          <select
            value={session.day}
            onChange={(e) => updateSession(weekId, session.id, { day: e.target.value })}
            className="text-xs font-bold bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 px-2 py-1 rounded-md border-none focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer"
          >
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d.slice(0, 3)}
              </option>
            ))}
          </select>

          {/* Focus (inline editable) */}
          <input
            type="text"
            value={session.focus}
            onChange={(e) => updateSession(weekId, session.id, { focus: e.target.value })}
            className="text-sm font-semibold bg-transparent border-none focus:outline-none focus:ring-0 flex-1 min-w-0 placeholder:text-slate-300"
            placeholder="Skupienie sesji..."
          />
        </div>

        <div className="flex items-center gap-0.5 ml-2">
          <span className="text-[10px] text-slate-400 mr-1">
            {totalCount}
          </span>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 text-slate-400 hover:text-slate-600 transition"
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          <button
            onClick={() => duplicateSession(weekId, session.id)}
            className="p-1 text-slate-400 hover:text-brand-500 transition"
            title="Duplikuj sesję"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={() => removeSession(weekId, session.id)}
            className="p-1 text-slate-400 hover:text-red-500 transition"
            title="Usuń sesję"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* ── Content (exercises + warmup) ──── */}
      {!collapsed && (
        <div className="p-2 space-y-1 animate-fade-in">

          {/* ── Warmup Section ───────────────── */}
          <div className="rounded-lg border border-orange-200 dark:border-orange-900/50 bg-orange-50/50 dark:bg-orange-950/20 overflow-hidden">
            <button
              onClick={() => setWarmupCollapsed(!warmupCollapsed)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-100/50 dark:hover:bg-orange-950/30 transition"
            >
              <span className="flex items-center gap-1.5">
                <Flame size={13} />
                Rozgrzewka
                <span className="text-[10px] font-normal text-orange-400 dark:text-orange-500">
                  ({warmups.length})
                </span>
              </span>
              {warmupCollapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
            </button>

            {!warmupCollapsed && (
              <div className="px-2 pb-2 space-y-1">
                {warmups.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleWarmupDragEnd}
                  >
                    <SortableContext
                      items={warmups.map((e) => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {warmups.map((ex, idx) => (
                        <ExerciseRow
                          key={ex.id}
                          exercise={ex}
                          index={idx}
                          onUpdate={(updates) =>
                            updateWarmupExercise(weekId, session.id, ex.id, updates)
                          }
                          onRemove={() => removeWarmupExercise(weekId, session.id, ex.id)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-2 text-[10px] text-orange-300 dark:text-orange-700">
                    Brak ćwiczeń rozgrzewkowych
                  </div>
                )}

                {showWarmupSearch ? (
                  <div className="mt-1">
                    <ExerciseSearch
                      onSelect={handleAddWarmupExercise}
                      onClose={() => setShowWarmupSearch(false)}
                      warmupOnly
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setShowWarmupSearch(true)}
                    className="w-full mt-0.5 py-1 rounded-md border border-dashed border-orange-200 dark:border-orange-800 text-orange-400 hover:text-orange-500 hover:border-orange-400 text-[11px] font-medium transition flex items-center justify-center gap-1"
                  >
                    <Plus size={12} />
                    Dodaj rozgrzewkę
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Main Exercises ────────────────── */}
          {session.exercises.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={session.exercises.map((e) => e.id)}
                strategy={verticalListSortingStrategy}
              >
                {session.exercises.map((ex, idx) => (
                  <ExerciseRow
                    key={ex.id}
                    exercise={ex}
                    index={idx}
                    onUpdate={(updates) =>
                      updateExercise(weekId, session.id, ex.id, updates)
                    }
                    onRemove={() => removeExercise(weekId, session.id, ex.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-4 text-xs text-slate-400">
              Brak ćwiczeń
            </div>
          )}

          {/* Add exercise */}
          {showSearch ? (
            <div className="mt-2">
              <ExerciseSearch
                onSelect={handleAddExercise}
                onClose={() => setShowSearch(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setShowSearch(true)}
              className="w-full mt-1 py-1.5 rounded-lg border border-dashed border-slate-200 dark:border-slate-700 text-slate-400 hover:text-brand-500 hover:border-brand-400 text-xs font-medium transition flex items-center justify-center gap-1"
            >
              <Plus size={14} />
              Dodaj ćwiczenie
            </button>
          )}
        </div>
      )}
    </div>
  );
}
